import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  Slide,
} from "@mui/material";
import { forwardRef, useEffect, useState } from "react";
import {
  hasProjectKnowledgeXlsxBlockedFile,
  normalizeUploadFiles,
  PROJECT_KNOWLEDGE_XLSX_ERROR,
} from "../../../core/services/fileUpload.service";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../pages/Home/constants/homeGlass";
import UploadFileDropZone from "./UploadFileDropZone";
import SaveProgressIndicator from "./SaveProgressIndicator";

const SlideDown = forwardRef(function SlideDown(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function UploadKnowledgeBaseModal({
  open,
  onClose,
  onUpload,
  loading = false,
  uploadProgress = null,
  forProjectUpload = false,
}) {
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setFileError(null);
    }
  }, [open]);

  const selectFiles = (nextFiles) => {
    setFileError(null);
    const selectedFiles = normalizeUploadFiles(nextFiles);

    if (!selectedFiles.length) {
      setFiles([]);
      return;
    }
    if (forProjectUpload && hasProjectKnowledgeXlsxBlockedFile(selectedFiles)) {
      setFiles([]);
      setFileError(PROJECT_KNOWLEDGE_XLSX_ERROR);
      return;
    }
    setFiles(selectedFiles);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    if (forProjectUpload && hasProjectKnowledgeXlsxBlockedFile(files)) {
      setFileError(PROJECT_KNOWLEDGE_XLSX_ERROR);
      return;
    }
    await onUpload(files);
    setFiles([]);
    setFileError(null);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      TransitionComponent={SlideDown}
      maxWidth={false}
      BackdropProps={{
        sx: {
          background:
            "radial-gradient(circle at 45% 35%, rgba(219, 165, 41, 0.16), transparent 32%), radial-gradient(circle at 62% 58%, rgba(47, 64, 34, 0.28), rgba(9, 12, 8, 0.88) 68%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
        },
      }}
      PaperProps={{
        sx: {
          width: "min(506px, 92vw)",
          borderRadius: { xs: "20px", sm: "24px" },
          position: "relative",
          background:
            "linear-gradient(145deg, rgba(48, 51, 39, 0.88), rgba(11, 16, 11, 0.96))",
          border: HOME_GLASS_BORDER,
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.16), 0 24px 70px rgba(0,0,0,0.46)",
          color: "#fff",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "0 18% auto",
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
            opacity: 0.8,
            pointerEvents: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 3, sm: "42px 40px" },
          boxSizing: "border-box",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 24, sm: 27 },
            fontWeight: 700,
            lineHeight: 1.2,
            mb: 1.4,
          }}
        >
          Upload Knowledge Base
        </Typography>

        <Typography
          sx={{
            fontSize: 16,
            color: "rgba(255, 255, 255, 0.66)",
            mb: 3.4,
          }}
        >
          Select files to upload
        </Typography>

        <UploadFileDropZone
          inputId="kb-upload"
          files={files}
          hasError={!!fileError}
          uploading={loading}
          onFileSelect={selectFiles}
          browseText="or click to browse"
          changeFileText="click to choose another"
          emptyTitle="Drop files here"
          dragTitle="Release files here"
          selectedTitle="Files ready to upload"
        />

        {fileError ? (
          <Typography
            sx={{
              fontSize: 13,
              color: "#ff8b8b",
              mb: 2,
              textAlign: "center",
            }}
          >
            {fileError}
          </Typography>
        ) : null}

        <Box sx={{ width: "100%", mt: loading && !forProjectUpload ? 2.4 : 0 }}>
          <SaveProgressIndicator
            active={loading && !forProjectUpload}
            progress={uploadProgress}
            accentColor="#dba529"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            mt: 3.8,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              px: 3,
              py: 1.05,
              minWidth: 110,
              background: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.74)",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              transition:
                "background 180ms ease, border-color 180ms ease, color 180ms ease",
              "&:hover": {
                background: "rgba(255,255,255,0.09)",
                borderColor: "rgba(255,255,255,0.28)",
                color: "#fff",
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.34)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpload}
            disabled={!files.length || !!fileError || loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
              ) : null
            }
            sx={{
              px: 3.5,
              py: 1.05,
              minWidth: 110,
              background: "linear-gradient(135deg, #dba529, #b48213)",
              color: "white",
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 10px 24px rgba(219, 165, 41, 0.24)",
              transition:
                "background 180ms ease, box-shadow 180ms ease, transform 180ms ease",
              "&:hover": {
                transform: "translateY(-2px)",
                background: "linear-gradient(135deg, #efbe36, #c89519)",
                boxShadow: "0 12px 28px rgba(219, 165, 41, 0.32)",
              },
              "&.Mui-disabled": {
                background: "rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.34)",
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}